import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import { isEsimUpsellTicketId } from 'loyalty/lib/esims/esimUpsellTicketUtils'

type EsimOrderResult =
  paths['/api/lux-loyalty/esim/orders']['get']['responses']['200']['content']['application/json']['result']['orders'][number]

type EsimPromoCodeResult =
  paths['/api/lux-loyalty/esim/order/{orderId}']['get']['responses']['200']['content']['application/json']['result']

type OrderDetailsEsimBenefit =
  paths['/api/lux-loyalty/customer/{customerId}/order/{orderId}']['get']['responses']['200']['content']['application/json']['result']['esimBenefit']

type EsimUpsellCatalogResult =
  paths['/api/lux-loyalty/esim/upsell/catalog']['get']['responses']['200']['content']['application/json']['result']

type EsimUpsellCatalogTicket = EsimUpsellCatalogResult['tickets'][number]

function mapEsimUpsellCatalogTicket(ticket: EsimUpsellCatalogTicket): App.LoyaltyEsimUpsellCatalogTicket | null {
  const { fromESimDataPackage, toESimDataPackage } = ticket.bookingOptions
  if (!isEsimUpsellTicketId(fromESimDataPackage) || !isEsimUpsellTicketId(toESimDataPackage)) {
    return null
  }

  return {
    id: ticket.id,
    name: ticket.name,
    price: ticket.price,
    memberPrice: ticket.memberPrice,
    memberPriceCurrency: ticket.memberPriceCurrency,
    discount: ticket.discount,
    discounts: ticket.discounts,
    bookingOptions: {
      fromESimDataPackage,
      toESimDataPackage,
    },
    badgeMessageId: ticket.badgeMessageId,
  }
}

export function mapLoyaltyEsimUpsellCatalog(catalog: EsimUpsellCatalogResult): App.LoyaltyEsimUpsellCatalog {
  return {
    esimOfferId: catalog.esimOfferId,
    currency: catalog.currency,
    tickets: catalog.tickets
      .map(mapEsimUpsellCatalogTicket)
      .filter((ticket): ticket is App.LoyaltyEsimUpsellCatalogTicket => ticket !== null),
  }
}

export function mapLoyaltyEsimBenefit(benefit: EsimOrderResult): App.LoyaltyEsimBenefit {
  return {
    orderId: benefit.orderId,
    customerId: benefit.customerId,
    countryCodes: benefit.countryCodes,
    tier: benefit.tier,
    packageSizeGB: benefit.packageSizeGB,
    packageSize: benefit.packageSize,
    unlimitedDays: benefit.unlimitedDays,
    status: benefit.status,
    departureDate: benefit.departureDate,
    endDate: benefit.endDate,
    promocode: benefit.promocode,
    partner: benefit.partner,
  }
}

export function mapLoyaltyEsimPromoCode(result: EsimPromoCodeResult): App.LoyaltyEsimBenefit {
  return mapLoyaltyEsimBenefit(result)
}

export function mapOrderLoyaltyEsimBenefit(
  benefit: NonNullable<OrderDetailsEsimBenefit>,
): App.OrderLoyaltyEsimBenefit {
  return {
    packageSize: benefit.packageSize,
    tier: benefit.tier,
    packageSizeGB: benefit.packageSizeGB,
    unlimitedDays: benefit.unlimitedDays,
    status: benefit.status,
    countryCodes: benefit.countryCodes,
    departureDate: benefit.departureDate,
    endDate: benefit.endDate,
  }
}

export function mapLoyaltyEsimBenefitToOrderEsimBenefit(
  benefit: App.LoyaltyEsimBenefit,
): App.OrderLoyaltyEsimBenefit {
  return {
    packageSize: benefit.packageSize,
    tier: benefit.tier,
    packageSizeGB: benefit.packageSizeGB,
    unlimitedDays: benefit.unlimitedDays,
    status: benefit.status,
    countryCodes: benefit.countryCodes,
    departureDate: benefit.departureDate,
    endDate: benefit.endDate,
  }
}
